/**
 * computeEndTime — Apr 30 2026 (App_Change_List #18)
 *
 * Adds a duration (in minutes) to a "HH:MM" or "HH:MM:SS" start string and
 * returns the resulting "HH:MM" end-time string. Used to display a game's
 * end time alongside its start time and duration on every surface that
 * shows the start time (dashboard, schedule, per-game clocks, etc.).
 *
 * Why a dedicated utility (instead of calling Date methods inline at every
 * site): the math itself is one-liner trivial, but doing it inline in JSX
 * means each callsite has to re-parse the time string, handle
 * minute-overflow, handle midnight wrap-around, and decide what to render
 * if the input is missing. Centralising those decisions here keeps the
 * call sites tidy ("end at X") and ensures the policy is consistent
 * (e.g. how we handle "duration is missing" — return null so the caller
 * suppresses the end-time display gracefully).
 *
 * Notes on the wrap-around:
 *   - A game starting at 22:00 with a 180-minute duration ends at 01:00
 *     (next day). We return "01:00" — NOT "25:00", which would render
 *     incorrectly through formatTime, and NOT "01:00 (next day)" since
 *     the surrounding UI already shows the game's date and any user
 *     reading "starts 10 PM, ends 1 AM" understands the carry-over.
 *
 *   - A duration that's negative or zero or non-numeric returns null —
 *     same policy as formatDuration. The caller will typically just
 *     render the start time without an "ends at" suffix.
 *
 *   - Missing/empty start time also returns null.
 *
 * Returns: "HH:MM" string (24-hour, zero-padded) ready to feed into
 *          formatTime() for 12h/24h display, or null if either input
 *          is missing/invalid.
 */
export function computeEndTime(startTimeStr, durationMinutes) {
  if (!startTimeStr || typeof startTimeStr !== 'string') return null;
  const dur = Number(durationMinutes);
  if (!Number.isFinite(dur) || dur <= 0) return null;
  // Apr 30 2026 audit (Batch H, audit 7/7) — floor to integer minutes
  // so a fractional duration value (e.g. 90.5) doesn't produce a
  // fractional minute output like "23:30.5". Backend enforces
  // integer minutes at save time but a future bug or direct DB edit
  // could land non-integers here; defensive flooring is cheap.
  const durInt = Math.floor(dur);

  const parts = startTimeStr.split(':').map(Number);
  if (parts.length < 2) return null;
  const [h, m] = parts;
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;

  // Minutes-since-midnight arithmetic. Modulo 24h handles the wrap-around
  // for late-night games naturally (e.g. 22:00 + 180min → 25:00 → 01:00).
  const startMinutes = h * 60 + m;
  const endMinutes   = (startMinutes + durInt) % (24 * 60);
  const endH = Math.floor(endMinutes / 60);
  const endM = endMinutes % 60;
  return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
}
